import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Blocking Terms & Conditions gate for the shop portal.
 *
 * When a shop owner opens the Business Portal, we check each order-store they own
 * against the current partner T&C version. Any store the current user can sign for
 * but hasn't yet → a full-screen, non-dismissable modal that shows the terms, and
 * requires a ticked acknowledgement, a typed full-name signature, AND a drawn
 * on-screen signature before they can continue. Signed acceptance is stored per
 * shop for admins to see. Renders nothing when there is nothing to sign.
 */

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface PendingStore { id: number; name: string }
interface TermsDoc { version: string; title: string; content: string; effectiveDate: string | null }

export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const point = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  }, []);

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const c = canvasRef.current!; const ctx = c.getContext("2d")!;
    drawing.current = true;
    const p = point(e);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    c.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = point(e);
    ctx.lineTo(p.x, p.y); ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(dirty.current ? canvasRef.current!.toDataURL("image/png") : null);
  };

  useEffect(() => {
    const c = canvasRef.current!; const ctx = c.getContext("2d")!;
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0f172a";
  }, []);

  const clear = () => {
    const c = canvasRef.current!; c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    dirty.current = false; onChange(null);
  };

  return (
    <div>
      <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          className="w-full h-[180px] touch-none rounded-lg"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-slate-400">Sign above with your mouse or finger</span>
        <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-rose-600 underline">Clear</button>
      </div>
    </div>
  );
}

export default function ShopTermsGate() {
  const [pending, setPending] = useState<PendingStore[] | null>(null);
  const [doc, setDoc] = useState<TermsDoc | null>(null);
  const [idx, setIdx] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const storesRes = await fetch("/api/order/my-stores", { headers: authHeaders() });
        const sd = await storesRes.json();
        const stores: PendingStore[] = Array.isArray(sd) ? sd : (sd.stores ?? []);
        if (!stores.length) { if (!cancelled) setPending([]); return; }
        const cur = await fetch("/api/shop/terms/current", { headers: authHeaders() });
        if (!cur.ok) { if (!cancelled) setPending([]); return; }
        const document = await cur.json() as TermsDoc;
        const statuses = await Promise.all(stores.map(async (s) => {
          const st = await fetch(`/api/shop/terms/status?storeId=${s.id}`, { headers: authHeaders() }).then((r) => r.json()).catch(() => null);
          // A prospectively accepted, effective Model A agreement supersedes the old signature gate;
          // a draft, pending offer or mere continued use never does. Ordering receives no new gate.
          const agency=st?.accepted===false?await fetch(`/api/agency/status?audience=shop&storeId=${s.id}`,{headers:authHeaders()}).then(r=>r.ok?r.json():null).catch(()=>null):null;
          return { ...s, accepted: st?.accepted || !!agency?.governingAgreement, canSign: st?.canSign };
        }));
        const unsigned = statuses.filter((s) => s.accepted === false && s.canSign).map((s) => ({ id: s.id, name: s.name }));
        if (!cancelled) { setDoc(document); setPending(unsigned); }
      } catch { if (!cancelled) setPending([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  const resetForm = () => { setAgreed(false); setName(""); setSignature(null); setScrolledEnd(false); setError(null); };

  const submit = async () => {
    if (!pending || !doc) return;
    const store = pending[idx];
    setSubmitting(true); setError(null);
    try {
      const res = await fetch("/api/shop/terms/accept", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ storeId: store.id, signedName: name.trim(), signatureDataUrl: signature }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Could not save your acceptance. Please try again.");
      }
      // advance to the next unsigned store, or finish
      if (idx + 1 < pending.length) { setIdx(idx + 1); resetForm(); }
      else { setPending([]); }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!pending || pending.length === 0 || !doc) return null;

  const store = pending[idx];
  const multi = pending.length > 1;
  const canAccept = agreed && name.trim().length >= 2 && !!signature && !submitting;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-3 sm:p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[94vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-7 py-4 border-b border-slate-100">
          <h2 className="text-lg font-extrabold text-slate-900">{doc.title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Version {doc.version}{doc.effectiveDate ? ` · Effective ${doc.effectiveDate}` : ""} · Signing for <span className="font-semibold text-slate-700">{store.name}</span>
            {multi ? <span className="text-slate-400"> ({idx + 1} of {pending.length})</span> : null}
          </p>
          <p className="text-xs text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
            You must read and sign these terms before you can manage orders.
          </p>
        </div>

        <div
          className="flex-1 overflow-y-auto px-5 sm:px-7 py-4 text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolledEnd(true);
          }}
        >
          {doc.content}
        </div>

        <div className="border-t border-slate-100 px-5 sm:px-7 py-4 space-y-3 bg-slate-50/60">
          {!scrolledEnd && (
            <p className="text-xs text-slate-400">Please scroll to the end of the terms to continue.</p>
          )}
          <label className={`flex items-start gap-2 text-sm ${scrolledEnd ? "text-slate-800" : "text-slate-400"}`}>
            <input type="checkbox" className="mt-0.5" disabled={!scrolledEnd} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>I have read and agree to these Terms and Conditions, and I have authority to accept them on behalf of {store.name}.</span>
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Full name (typed signature)</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                disabled={!scrolledEnd}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Signature</label>
              {scrolledEnd
                ? <SignaturePad onChange={setSignature} />
                : <div className="h-[180px] rounded-lg border-2 border-dashed border-slate-200 bg-slate-100" />}
            </div>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={!canAccept}
              className="px-5 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Saving…" : multi && idx + 1 < pending.length ? "Agree & Sign — Next" : "Agree & Sign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
